import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, FileText, AlertTriangle, CheckCircle, XCircle, ArrowLeft } from "lucide-react";

interface LicenseData {
  [key: string]: string;
}

interface ImportResult {
  successful: number;
  failed: number;
  errors: { row: number; error: string }[];
}

// All fields with their display labels and DB keys
const requiredFields: Record<string, string> = {
  name: "License Name",
  applicationName: "Application Name",
  startDate: "Start Date",
  totalSeats: "Total Seats",
  cost: "Cost",
};

const optionalFields: Record<string, string> = {
  renewalDate: "Renewal Date",
  billingCycle: "Billing Cycle",
  currency: "Currency",
  assignedEmployeeEmail: "Assign to Employee (Email)",
  description: "Description",
};

// Aliases for auto-mapping: key = field name, value = accepted CSV header variants (lowercase)
const FIELD_ALIASES: Record<string, string[]> = {
  name: ["name", "license name", "license_name", "licensename", "license"],
  applicationName: ["applicationname", "application name", "application_name", "app name", "app_name", "appname", "application", "app"],
  startDate: ["startdate", "start date", "start_date", "start", "startdate (yyyy-mm-dd)", "start date (yyyy-mm-dd)"],
  renewalDate: ["renewaldate", "renewal date", "renewal_date", "expiry date", "expiration date", "expiry", "expiration", "end date", "renewaldate (yyyy-mm-dd)"],
  totalSeats: ["totalseats", "total seats", "total_seats", "seats", "num seats", "num_seats", "quantity"],
  cost: ["cost", "price", "amount", "annual cost", "monthly cost"],
  billingCycle: ["billingcycle", "billing cycle", "billing_cycle", "cycle", "billing"],
  currency: ["currency", "curr", "iso currency"],
  assignedEmployeeEmail: ["assignedemployeeemail", "assigned employee email", "employee email", "user email", "assignedemail"],
  description: ["description", "desc", "notes", "note"],
};

export default function DataImportPage() {
  const [csvData, setCsvData] = useState<LicenseData[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [importStep, setImportStep] = useState<"upload" | "map" | "preview" | "result">("upload");
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Derive auto-mappings from CSV headers
  const autoMapFields = (headers: string[]): Record<string, string> => {
    const mappings: Record<string, string> = {};
    const allFieldKeys = [...Object.keys(requiredFields), ...Object.keys(optionalFields)];
    allFieldKeys.forEach((field) => {
      const aliases = FIELD_ALIASES[field] ?? [field.toLowerCase()];
      const match = headers.find((h) => aliases.includes(h.toLowerCase().trim()));
      if (match) mappings[field] = match;
    });
    return mappings;
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (licenseData: LicenseData[]) => {
      return await apiRequest("/api/unified-import", {
        method: "POST",
        body: {
          applications: [],
          licenses: licenseData,
          fileName: uploadedFileName || "import.csv",
        },
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: async (response: any) => {
      const successful = response?.licenses?.successful ?? response?.summary?.totalSuccessful ?? 0;
      const failed = response?.licenses?.failed ?? response?.summary?.totalFailed ?? 0;
      const rawErrors: any[] = response?.licenses?.errors ?? [];
      const errors = rawErrors.map((e: any, i: number) => ({
        row: i + 1,
        error: e.error ?? String(e),
      }));

      setImportResult({ successful, failed, errors });
      setImportStep("result");
      setIsProcessing(false);

      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["/api/licenses"], type: "all" }),
        queryClient.refetchQueries({ queryKey: ["/api/applications"], type: "all" }),
        queryClient.refetchQueries({ queryKey: ["/api/dashboard/stats"], type: "all" }),
      ]);
    },
    onError: (error: any) => {
      const firstError =
        error?.response?.data?.licenses?.errors?.[0]?.error ??
        error?.response?.data?.message ??
        error?.message ??
        "Import failed. Please check your data.";

      setImportResult({
        successful: 0,
        failed: csvData.length,
        errors: [{ row: 0, error: firstError }],
      });
      setImportStep("result");
      setIsProcessing(false);
    },
  });

  const resetImportState = () => {
    setIsImportDialogOpen(false);
    setImportStep("upload");
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMappings({});
    setValidationErrors([]);
    setIsProcessing(false);
    setImportResult(null);
    setUploadedFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Parse a single CSV line respecting quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      // Normalize line endings
      const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim());

      if (lines.length < 2) {
        toast({ title: "Invalid file", description: "CSV must have a header row and at least one data row.", variant: "destructive" });
        return;
      }

      const headers = parseCSVLine(lines[0]);
      const dataRows = lines.slice(1).map((line) => {
        const values = parseCSVLine(line);
        const row: LicenseData = {};
        headers.forEach((header, idx) => { row[header] = values[idx] ?? ""; });
        return row;
      }).filter((row) => Object.values(row).some((v) => v.trim() !== ""));

      if (dataRows.length === 0) {
        toast({ title: "No data rows", description: "The CSV file has no valid data rows.", variant: "destructive" });
        return;
      }

      setCsvHeaders(headers);
      setCsvData(dataRows);
      setFieldMappings(autoMapFields(headers)); // Auto-map by header name
      setImportStep("map");
      setIsImportDialogOpen(true);
    };
    reader.readAsText(file);
  };

  const validateMappings = (): boolean => {
    const errors: string[] = [];

    // Check all required fields are mapped
    Object.entries(requiredFields).forEach(([field, label]) => {
      const col = fieldMappings[field];
      if (!col || col === "not-mapped" || !csvHeaders.includes(col)) {
        errors.push(`"${label}" is required but not mapped to any column.`);
      }
    });

    // Check for duplicate column assignments across required fields
    const usedCols = new Map<string, string>(); // colName → fieldLabel
    Object.entries({ ...requiredFields, ...optionalFields }).forEach(([field, label]) => {
      const col = fieldMappings[field];
      if (!col || col === "not-mapped") return;
      if (usedCols.has(col)) {
        errors.push(`Column "${col}" is mapped to both "${usedCols.get(col)}" and "${label}". Each column can only be used once.`);
      } else {
        usedCols.set(col, label);
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Build the final import payload from the mappings
  const buildPayload = (): LicenseData[] => {
    return csvData.map((row) => {
      const out: LicenseData = {};
      Object.keys(requiredFields).forEach((field) => {
        const col = fieldMappings[field];
        if (col && col !== "not-mapped") out[field] = (row[col] ?? "").trim();
      });
      Object.keys(optionalFields).forEach((field) => {
        const col = fieldMappings[field];
        if (col && col !== "not-mapped" && row[col]?.trim()) out[field] = row[col].trim();
      });
      return out;
    });
  };

  // Preview: first 5 rows with applied mappings (for the preview step)
  const previewRows = () => {
    const payload = buildPayload();
    return payload.slice(0, 5);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      toast({ title: "Invalid file type", description: "Please drop a CSV file.", variant: "destructive" });
      return;
    }
    // Reuse existing file-read logic via a synthetic event-like object
    const syntheticEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleFileUpload(syntheticEvent);
  };

  const handleProceedToPreview = () => {
    if (validateMappings()) setImportStep("preview");
  };

  const handleImport = async () => {
    const payload = buildPayload();
    setIsProcessing(true);
    await importMutation.mutateAsync(payload).catch(() => {});
  };

  // Template download — 10 realistic sample rows with clean header names
  const downloadTemplate = () => {
    const headers = ["name", "applicationName", "startDate", "renewalDate", "totalSeats", "cost", "currency", "billingCycle", "assignedEmployeeEmail", "description"];

    const rows = [
      ["Microsoft 365 Business", "Microsoft 365", "2024-01-01", "2025-01-01", "50", "6000", "USD", "Yearly", "admin@company.com", "Office suite for the entire org"],
      ["Slack Pro", "Slack", "2024-02-01", "2025-02-01", "80", "960", "USD", "Yearly", "ops@company.com", "Team messaging platform"],
      ["Adobe Creative Cloud", "Adobe Creative Cloud", "2024-03-15", "2025-03-15", "10", "5994", "USD", "Yearly", "design@company.com", "Design tools for creative team"],
      ["Salesforce Enterprise", "Salesforce", "2024-01-01", "2025-01-01", "25", "37500", "USD", "Yearly", "sales@company.com", "CRM for sales team"],
      ["Zoom Business", "Zoom", "2024-04-01", "2025-04-01", "100", "1990", "USD", "Yearly", "it@company.com", "Video conferencing"],
      ["GitHub Team", "GitHub", "2024-05-01", "2025-05-01", "30", "1188", "USD", "Yearly", "dev@company.com", "Source code management"],
      ["Figma Organization", "Figma", "2024-06-01", "2025-06-01", "15", "3600", "USD", "Yearly", "design@company.com", "UI/UX design collaboration"],
      ["Jira Software", "Jira", "2024-07-01", "2025-07-01", "40", "4800", "USD", "Yearly", "pm@company.com", "Project tracking for engineering"],
      ["HubSpot Marketing", "HubSpot", "2024-08-01", "2025-08-01", "5", "2400", "USD", "Monthly", "marketing@company.com", "Inbound marketing platform"],
      ["Notion Team", "Notion", "2024-09-01", "2025-09-01", "60", "1440", "USD", "Yearly", "ops@company.com", "Knowledge base and docs"],
    ];

    const escape = (v: string) => (v.includes(",") || v.includes('"') || v.includes("\n") ? `"${v.replace(/"/g, '""')}"` : v);
    const csv = [headers.join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "license-import-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const allPreviewFields = { ...requiredFields, ...optionalFields };
  const mappedPreviewFields = Object.entries(allPreviewFields).filter(([field]) => {
    const col = fieldMappings[field];
    return col && col !== "not-mapped";
  });

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-800">License Import</h2>
        <p className="text-sm text-slate-500 mt-1">Import your software licenses from a CSV file</p>
      </div>

      <Tabs defaultValue="import">
        <TabsList className="mb-4">
          <TabsTrigger value="import">Import Licenses</TabsTrigger>
          <TabsTrigger value="help">Help & Guide</TabsTrigger>
        </TabsList>

        <TabsContent value="import">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Upload className="h-5 w-5 mr-2 text-primary" />
                  Import Licenses
                </CardTitle>
                <CardDescription>Upload a CSV file with your license data</CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className={`rounded-lg p-8 text-center cursor-pointer transition-all duration-150 ${
                    isDragging
                      ? "bg-primary/5 scale-[1.01]"
                      : "bg-slate-50 hover:bg-primary/5"
                  }`}
                  style={{
                    backgroundImage: isDragging
                      ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%236366f1' stroke-width='2' stroke-dasharray='4 6' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`
                      : `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23cbd5e1' stroke-width='2' stroke-dasharray='4 6' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`,
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  onDrop={handleDrop}
                >
                  <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} className="hidden" />
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className={`rounded-full p-3 transition-colors ${isDragging ? "bg-primary/20" : "bg-primary/10"}`}>
                      <Upload className={`h-7 w-7 transition-colors ${isDragging ? "text-primary" : "text-primary/70"}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-700">
                        {isDragging ? "Drop your CSV file here" : "Drag & drop your CSV file"}
                      </h3>
                      <p className="text-sm text-slate-400 mt-1">or <span className="text-primary underline underline-offset-2">click to browse</span></p>
                    </div>
                    <p className="text-xs text-slate-400">CSV files only</p>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary" />
                  Quick Start Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {[
                    { n: 1, title: "Download Template", desc: "Get the CSV template with example data and correct column names" },
                    { n: 2, title: "Fill Your Data", desc: "Add license data — column names must match the template exactly for auto-mapping" },
                    { n: 3, title: "Upload & Verify", desc: "Upload your file, review the field mapping and data preview, then import" },
                  ].map(({ n, title, desc }) => (
                    <div key={n} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-medium text-sm">{n}</span>
                      </div>
                      <div>
                        <h4 className="font-medium">{title}</h4>
                        <p className="text-sm text-slate-500">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertTriangle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-700 text-sm">
                    <strong>Required:</strong> name, applicationName, startDate, totalSeats, cost
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="help">
          <Card>
            <CardHeader><CardTitle>Import Help & Field Reference</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-medium mb-3">Required Fields</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {Object.entries(requiredFields).map(([field, label]) => (
                    <div key={field} className="border rounded p-3">
                      <Badge className="mb-1">Required</Badge>
                      <h4 className="font-medium">{label}</h4>
                      <p className="text-xs text-slate-500 font-mono mt-1">{field}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-medium mb-3">Optional Fields</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {Object.entries(optionalFields).map(([field, label]) => (
                    <div key={field} className="border rounded p-3">
                      <Badge variant="outline" className="mb-1">Optional</Badge>
                      <h4 className="font-medium">{label}</h4>
                      <p className="text-xs text-slate-500 font-mono mt-1">{field}</p>
                    </div>
                  ))}
                </div>
              </div>
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700 text-sm">
                  <strong>Date formats accepted:</strong> YYYY-MM-DD (recommended), DD-MM-YYYY, MM/DD/YYYY, DD/MM/YYYY
                </AlertDescription>
              </Alert>
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-sm">
                  <strong>Tip:</strong> Use the exact column names from the template (e.g. <code>name</code>, <code>applicationName</code>) for automatic field detection. If your column names differ, you'll map them manually in the next step.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={(open) => { if (!open && !isProcessing) resetImportState(); }}>
        <DialogContent className="max-w-4xl flex flex-col" style={{ maxHeight: "90vh" }}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {importStep === "map" && `Map Fields — ${csvData.length} rows from "${uploadedFileName}"`}
              {importStep === "preview" && `Preview Import Data — ${csvData.length} rows`}
              {importStep === "result" && "Import Complete"}
            </DialogTitle>
          </DialogHeader>

          {/* ── STEP 1: Map fields ── */}
          {importStep === "map" && (
            <>
              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto space-y-6 pr-1">
                <p className="text-sm text-slate-500">
                  Fields with a <span className="text-green-600 font-medium">✓ Auto-detected</span> tag were matched from your CSV headers. Review and adjust if needed.
                </p>

                <div className="grid gap-3">
                  <div className="grid grid-cols-2 text-xs font-medium text-slate-500 px-1 pb-1 border-b">
                    <span>Field</span>
                    <span>CSV Column</span>
                  </div>

                  {Object.entries(requiredFields).map(([field, label]) => {
                    const isAutoMapped = fieldMappings[field] && csvHeaders.includes(fieldMappings[field]);
                    return (
                      <div key={field} className="grid grid-cols-2 items-center gap-2">
                        <div className="flex items-center gap-2">
                          <Badge>Required</Badge>
                          <span className="text-sm font-medium">{label}</span>
                          {isAutoMapped && <span className="text-xs text-green-600">✓ Auto-detected</span>}
                        </div>
                        <Select
                          value={fieldMappings[field] ?? ""}
                          onValueChange={(val) => setFieldMappings((prev) => ({ ...prev, [field]: val }))}
                        >
                          <SelectTrigger className={`${isAutoMapped ? "border-green-300 bg-green-50" : ""}`}>
                            <SelectValue placeholder="Select column…" />
                          </SelectTrigger>
                          <SelectContent>
                            {csvHeaders.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}

                  <div className="border-t my-1" />

                  {Object.entries(optionalFields).map(([field, label]) => {
                    const isAutoMapped = !!(fieldMappings[field] && csvHeaders.includes(fieldMappings[field]));
                    return (
                      <div key={field} className="grid grid-cols-2 items-center gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">Optional</Badge>
                          <span className="text-sm">{label}</span>
                          {isAutoMapped && <span className="text-xs text-green-600">✓ Auto-detected</span>}
                        </div>
                        <Select
                          value={fieldMappings[field] ?? "not-mapped"}
                          onValueChange={(val) => setFieldMappings((prev) => ({ ...prev, [field]: val }))}
                        >
                          <SelectTrigger className={`${isAutoMapped ? "border-green-300 bg-green-50" : ""}`}>
                            <SelectValue placeholder="Not mapped" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not-mapped">— Not mapped —</SelectItem>
                            {csvHeaders.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>

                {validationErrors.length > 0 && (
                  <Alert className="bg-red-50 border-red-200">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700">
                      <p className="font-medium mb-1">Please fix the following:</p>
                      <ul className="list-disc list-inside space-y-0.5 text-sm">
                        {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Sticky footer */}
              <div className="flex-shrink-0 flex justify-between pt-4 border-t mt-2">
                <Button variant="outline" onClick={resetImportState}>Cancel</Button>
                <Button onClick={handleProceedToPreview}>Preview Data →</Button>
              </div>
            </>
          )}

          {/* ── STEP 2: Preview ── */}
          {importStep === "preview" && (
            <>
              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                <p className="text-sm text-slate-500">
                  Showing the first {Math.min(5, csvData.length)} of {csvData.length} rows. Verify the values look correct before importing.
                </p>

                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">#</th>
                        {mappedPreviewFields.map(([field, label]) => (
                          <th key={field} className="px-3 py-2 text-left text-xs font-medium text-slate-500 whitespace-nowrap">
                            {label}
                            {requiredFields[field] ? <span className="text-red-400 ml-0.5">*</span> : null}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previewRows().map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-400 text-xs">{i + 1}</td>
                          {mappedPreviewFields.map(([field]) => (
                            <td key={field} className={`px-3 py-2 max-w-[160px] truncate ${!row[field] && requiredFields[field] ? "bg-red-50 text-red-600 text-xs" : ""}`}>
                              {row[field] || (requiredFields[field] ? <span className="text-red-400 italic">missing</span> : <span className="text-slate-300">—</span>)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {csvData.length > 5 && (
                  <p className="text-xs text-slate-400 text-center">…and {csvData.length - 5} more rows</p>
                )}
              </div>

              {/* Sticky footer — always visible */}
              <div className="flex-shrink-0 flex justify-between items-center pt-4 border-t mt-2">
                <Button variant="outline" onClick={() => setImportStep("map")} disabled={isProcessing}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Mapping
                </Button>
                <Button onClick={handleImport} disabled={isProcessing} size="lg">
                  {isProcessing ? (
                    <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" />Importing…</>
                  ) : (
                    `Import ${csvData.length} License${csvData.length === 1 ? "" : "s"}`
                  )}
                </Button>
              </div>
            </>
          )}

          {/* ── STEP 3: Result ── */}
          {importStep === "result" && importResult && (
            <div className="flex-1 overflow-y-auto space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{importResult.successful}</p>
                  <p className="text-sm text-green-600">Successfully imported</p>
                </div>
                <div className={`rounded-lg border p-4 text-center ${importResult.failed > 0 ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                  <XCircle className={`h-8 w-8 mx-auto mb-2 ${importResult.failed > 0 ? "text-red-400" : "text-slate-300"}`} />
                  <p className={`text-2xl font-bold ${importResult.failed > 0 ? "text-red-700" : "text-slate-400"}`}>{importResult.failed}</p>
                  <p className={`text-sm ${importResult.failed > 0 ? "text-red-600" : "text-slate-400"}`}>Failed</p>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">Error details:</p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {importResult.errors.slice(0, 20).map((e, i) => (
                      <div key={i} className="text-xs bg-red-50 border border-red-100 rounded px-3 py-2 text-red-700">
                        {e.row > 0 ? <span className="font-medium">Row {e.row}: </span> : null}{e.error}
                      </div>
                    ))}
                    {importResult.errors.length > 20 && (
                      <p className="text-xs text-slate-400 text-center">…and {importResult.errors.length - 20} more errors</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={resetImportState}>
                  {importResult.failed === 0 ? "Done" : "Close"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
